/**
 * Reserve participation tuning of a reserve (FCR) battery: a multiplier the
 * FCR service applies to every bid price of the unit (appliance config
 * `bid_price_factor`). A higher price means fewer accepted bids — more
 * self-use benefit, less reserve income; a lower price the opposite.
 *
 * Seven steps, left ("more self-use benefit") → right ("more reserve income").
 * The server accepts exactly these values (Sunergos.Schema.Appliance and
 * FCR.Bids.PriceFactor); keep the three lists in sync.
 */

export const RESERVE_FACTORS = [2.5, 1.67, 1.25, 1.0, 0.8, 0.62, 0.4] as const

export type ReserveFactor = typeof RESERVE_FACTORS[number]

/** Slider index of the default factor 1.00. */
export const RESERVE_DEFAULT_STEP = 3

export type ReserveBand = 'green' | 'yellow'

/** Which way a yellow step leans: toward self-use (left) or reserve income (right). */
export type ReserveWarning = 'self-use' | 'reserve' | null

/** Slider index of a stored factor; unset or unknown values read as the default. */
export function factorToStep(factor: unknown): number {
  const idx = RESERVE_FACTORS.indexOf(Number(factor) as ReserveFactor)
  return factor === undefined || factor === null || idx < 0 ? RESERVE_DEFAULT_STEP : idx
}

export function stepToFactor(step: number): ReserveFactor {
  return RESERVE_FACTORS[Math.min(Math.max(Math.round(step), 0), RESERVE_FACTORS.length - 1)]
}

/** The three middle steps (×1.25 / ×1.00 / ×0.8) are green, the outer four yellow. */
export function reserveBand(step: number): ReserveBand {
  return Math.abs(step - RESERVE_DEFAULT_STEP) <= 1 ? 'green' : 'yellow'
}

export function reserveWarning(step: number): ReserveWarning {
  if (reserveBand(step) === 'green') return null
  return step < RESERVE_DEFAULT_STEP ? 'self-use' : 'reserve'
}

/** Value label of a step: the offset from the default, e.g. −2, 0, +1. */
export function reserveStepLabel(step: number): string {
  const offset = step - RESERVE_DEFAULT_STEP
  return offset > 0 ? `+${offset}` : offset < 0 ? `−${-offset}` : '0'
}
